import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  Autocomplete,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { toast } from 'react-toastify';
import DynamicTable from 'src/components/shared/DynamicTable';
import CheckDeliveryRoom from './CheckDeliveryRoom';
import Services from 'src/services/Services';
import { CutomerVehicleApprovalModal } from 'src/modals/CutomerVehicleApprovalModal';
import api from 'src/services/BaseService';

const SERVICE_OPTIONS = [
  { value: 'kid-car', label: 'Çocuk Araçları' },
  { value: 'pet-car', label: 'Evcil Hayvan Araçları' },
  { value: 'checkroom', label: 'Vestiyer Hizmeti' },
];

const statusLabels = {
  '1': 'Tamamlandı',
  '3': 'Onay Bekliyor',
  '4': 'Onaylandı',
  '99': 'Süre Aşıldı',
  'payment-waiting': 'Ödeme Bekleniyor',
};

const statusStyles = {
  '1': { backgroundColor: 'rgba(222,247,236,1)', color: 'rgba(3,84,63,1)' },
  '3': { backgroundColor: 'rgba(255,243,205,1)', color: 'rgba(133,94,0,1)' },
  '4': { backgroundColor: 'rgba(217,234,251,1)', color: 'rgba(13,60,97,1)' },
  '99': { backgroundColor: 'rgba(253,232,232,1)', color: 'rgba(155,28,28,1)' },
  'payment-waiting': {
    backgroundColor: 'rgba(255,243,205,1)',
    color: 'rgba(133,94,0,1)',
  },
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const getValueByPaths = (obj, paths) => {
  for (const path of paths) {
    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current == null) break;
      current = current[part];
    }

    if (current !== undefined && current !== null && current !== '') {
      return current;
    }
  }

  return null;
};

const normalizeId = (value) =>
  value === undefined || value === null || value === '' ? '' : String(value);

const getPointMallId = (point) =>
  normalizeId(
    getValueByPaths(point, [
      'mallID',
      'mallId',
      'details.mallID',
      'details.mallId',
      'office.mallID',
      'office.mallId',
    ])
  );

const getPointId = (point) =>
  normalizeId(getValueByPaths(point, ['id', 'pointID', 'poiID', 'officeID']));

const isEasyPoint = (point) =>
  String(
    getValueByPaths(point, [
      'isEasyPoint',
      'details.isEasyPoint',
      'office.isEasyPoint',
    ]) || '0'
  ) === '1';

const isMalKabulPoint = (point) =>
  String(
    getValueByPaths(point, [
      'isMalKabul',
      'details.isMalKabul',
      'office.isMalKabul',
    ]) || '0'
  ) === '1';

const preferNonMalKabulPoint = (candidatePoints = []) =>
  candidatePoints.find((point) => !isMalKabulPoint(point)) || candidatePoints[0] || null;

const buildMallQrLink = (poiId) =>
  poiId
    ? `https://qr.easypointapi.com/?chs=700x700&cht=qr&chl=https://easydesk.easypoint.com.tr/?poi=${poiId}`
    : '';

const buildVehicleStatus = (item) => {
  const confirmed = Number(item.confirmedDateTime);
  const durationSec = Number(item.duration);
  const endByConfirmed =
    Number.isFinite(confirmed) &&
    confirmed > 0 &&
    Number.isFinite(durationSec) &&
    durationSec > 0
      ? confirmed + durationSec
      : 0;
  const end =
    endByConfirmed > 0 ? endByConfirmed : Number(item.willDeliverDateTime);
  const isOverdue =
    item.status === '4' &&
    Number.isFinite(end) &&
    end > 0 &&
    moment().unix() > end;
  const isPaymentWaiting = item.isPaid !== '1';

  return isPaymentWaiting ? 'payment-waiting' : isOverdue ? '99' : item.status;
};

const renderStatusBadge = (statusValue, label) => (
  <span
    style={{
      padding: '4px 12px',
      borderRadius: '6px',
      fontSize: 14,
      fontWeight: 500,
      fontFamily: 'Inter',
      marginRight: '6px',
      whiteSpace: 'pre',
      ...(statusStyles[statusValue] || {
        backgroundColor: 'rgba(237,235,254,1)',
        color: 'rgba(85,33,181,1)',
      }),
    }}
  >
    {label}
  </span>
);

export default function EPBulkServiceManagement() {
  const parsedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isMobile = useMediaQuery('(max-width:768px)');

  const [malls, setMalls] = useState([]);
  const [points, setPoints] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [selectedMallId, setSelectedMallId] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [pendingApprovalRows, setPendingApprovalRows] = useState([]);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [pendingComplete, setPendingComplete] = useState(null);
  const [currentPoiId, setCurrentPoiId] = useState('');
  const [currentMallName, setCurrentMallName] = useState('');
  const [currentService, setCurrentService] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedData, setEditedData] = useState({
    checkroomID: '',
    inventoryCount: '',
    additionalNote: '',
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelCheckroomId, setCancelCheckroomId] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const selectedMall = useMemo(
    () => malls.find((mall) => normalizeId(mall.id) === normalizeId(selectedMallId)),
    [malls, selectedMallId]
  );
  const selectedMallOption = selectedMall || null;

  const fetchMeta = async () => {
    try {
      setMetaLoading(true);
      const [mallsRes, pointsRes] = await Promise.all([
        Services.GetMalls(parsedUser.token),
        Services.GetPoints(parsedUser.token),
      ]);
      setMalls(mallsRes?.data?.result || []);
      setPoints(pointsRes?.data?.result || []);
    } catch (error) {
      console.error(error);
      toast.error('AVM ve nokta bilgileri alınamadı.');
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  const resolvePoiIdForMall = (mall) => {
    if (!mall) return '';

    const officeIds = toArray(
      getValueByPaths(mall, ['offices', 'details.offices', 'result.offices'])
    )
      .map((office) => normalizeId(office?.id))
      .filter(Boolean);

    const pointsByOfficeIds = officeIds
      .map((officeId) =>
        points.find((point) => getPointId(point) === officeId)
      )
      .filter(Boolean);

    if (officeIds.length > 1) {
      const preferredEasyPoint = preferNonMalKabulPoint(
        pointsByOfficeIds.filter((point) => isEasyPoint(point))
      );
      if (preferredEasyPoint) {
        return getPointId(preferredEasyPoint);
      }
    }

    const preferredOfficePoint = preferNonMalKabulPoint(pointsByOfficeIds);
    if (preferredOfficePoint) {
      return getPointId(preferredOfficePoint);
    }

    const firstOfficeId = officeIds[0];
    if (firstOfficeId) {
      return firstOfficeId;
    }

    const mallId = normalizeId(mall.id);

    const pointByMallId = preferNonMalKabulPoint(points.filter(
      (point) => mallId && getPointMallId(point) === mallId
    ));
    if (pointByMallId && (officeIds.length <= 1 || isEasyPoint(pointByMallId))) {
      return getPointId(pointByMallId);
    }

    if (officeIds.length > 1) {
      const easyPointByMallId = preferNonMalKabulPoint(
        points.filter(
          (point) => mallId && getPointMallId(point) === mallId && isEasyPoint(point)
        )
      );
      if (easyPointByMallId) {
        return getPointId(easyPointByMallId);
      }
    }

    const pointByTitle = preferNonMalKabulPoint(points.filter(
      (point) =>
        String(point?.title || '').trim().toLowerCase() ===
        String(mall?.title || '').trim().toLowerCase()
    ));
    if (pointByTitle && (officeIds.length <= 1 || isEasyPoint(pointByTitle))) {
      return getPointId(pointByTitle);
    }

    if (officeIds.length > 1) {
      const easyPointByTitle = preferNonMalKabulPoint(
        points.filter(
          (point) =>
            isEasyPoint(point) &&
            String(point?.title || '')
              .trim()
              .toLowerCase()
              .replace(/^easy point -\s*/, '') ===
              String(mall?.title || '').trim().toLowerCase()
        )
      );
      if (easyPointByTitle) {
        return getPointId(easyPointByTitle);
      }
    }

    return '';
  };

  const selectedMallPoiId = useMemo(
    () => resolvePoiIdForMall(selectedMall),
    [selectedMall, points]
  );
  const selectedMallQrLink = useMemo(
    () => buildMallQrLink(selectedMallPoiId),
    [selectedMallPoiId]
  );

  const getVehicleOrdersByService = (poiId) => {
    if (selectedService === 'kid-car') {
      return Services.GetKidCarOrders(parsedUser.token, poiId);
    }

    return Services.GetPetCarOrders(parsedUser.token, poiId);
  };

  const mapVehicleRows = (items, mallName) => {
    const mapped = items.map((item, idx) => {
      const confirmed = Number(item.confirmedDateTime);
      const displayStatus = buildVehicleStatus(item);

      return {
        id: item.id,
        no: idx + 1,
        conversationID: item.paymentID || item.paymentId || '-',
        requestID: item.requestID || '-',
        locationInfo: mallName,
        name: item.name,
        lastname: item.lastname,
        vehicleID: item.vehicleID || '-',
        packageCount: 1,
        totalPrice: item.price ? `${item.price} ${item.currency}` : '-',
        rentalDuration: item.durationText,
        createdDateTime: moment
          .unix(Number(item.createdDateTime))
          .format('DD.MM.YYYY HH:mm'),
        createdDateTimeTs: Number(item.createdDateTime),
        confirmedDateTime:
          confirmed > 0 ? moment.unix(confirmed).format('DD.MM.YYYY HH:mm') : '-',
        confirmedDateTimeTs: confirmed > 0 ? confirmed : 0,
        status: item.status,
        paymentType: item.paymentType,
        displayStatus,
        item,
        isPaid: item.isPaid,
      };
    });

    const pending = mapped.filter(
      (row) =>
        row.status === '3' &&
        (row.isPaid === '1' || ["physical-pos", "cash"].includes(row.paymentType))
    );

    const visibleRows = mapped.filter(
      (row) =>
        row.isPaid === '1' ||
        (row.displayStatus === 'payment-waiting' &&
          ["physical-pos", "cash"].includes(row.paymentType))
    );

    setPendingApprovalRows(pending);
    setTableRows(visibleRows);
  };

  const mapCheckroomRows = (items, mallName) => {
    const sorted = items
      .map((item) => ({
        ...item,
        createdDateTime: Number(item.createdDateTime),
      }))
      .sort((a, b) => b.createdDateTime - a.createdDateTime)
      .map((item, idx) => ({
        id: item.checkroomID || item.id || idx,
        no: idx + 1,
        pointTitle: item.pointTitle || mallName,
        name: item.name || '-',
        lastname: item.lastname || '-',
        phone: item.phone || '-',
        checkroomID: item.checkroomID,
        inventoryCount: item.inventoryCount ?? '-',
        createdDateTime: item.createdDateTime,
        createdDateTimeLabel: moment
          .unix(Number(item.createdDateTime))
          .format('DD.MM.YYYY HH:mm'),
        status: item.status,
        additionalNote: item.additionalNote || '',
      }));

    setPendingApprovalRows([]);
    setTableRows(sorted);
  };

  const handleList = async () => {
    if (!selectedMall) {
      toast.error('Lütfen bir AVM seçiniz.');
      return;
    }

    if (!selectedService) {
      toast.error('Lütfen bir hizmet seçiniz.');
      return;
    }

    const poiId = resolvePoiIdForMall(selectedMall);

    if (!poiId) {
      toast.error('Seçilen AVM için nokta bilgisi bulunamadı.');
      return;
    }

    try {
      setListLoading(true);
      setCurrentPoiId(poiId);
      setCurrentMallName(selectedMall.title || '-');
      setCurrentService(selectedService);

      if (selectedService === 'checkroom') {
        const response = await Services.GetCheckRooms(
          parsedUser.token,
          null,
          null,
          null,
          poiId
        );
        mapCheckroomRows(response?.data?.result || [], selectedMall.title || '-');
        return;
      }

      const response = await getVehicleOrdersByService(poiId);
      mapVehicleRows(response?.data?.result || [], selectedMall.title || '-');
    } catch (error) {
      console.error(error);
      toast.error('Listeleme sırasında bir hata oluştu.');
      setTableRows([]);
      setPendingApprovalRows([]);
    } finally {
      setListLoading(false);
    }
  };

  const handleRefreshCurrentList = async () => {
    if (!currentService || !currentPoiId || !currentMallName) return;

    try {
      setListLoading(true);

      if (currentService === 'checkroom') {
        const response = await Services.GetCheckRooms(
          parsedUser.token,
          null,
          null,
          null,
          currentPoiId
        );
        mapCheckroomRows(response?.data?.result || [], currentMallName);
        return;
      }

      const response =
        currentService === 'kid-car'
          ? await Services.GetKidCarOrders(parsedUser.token, currentPoiId)
          : await Services.GetPetCarOrders(parsedUser.token, currentPoiId);

      mapVehicleRows(response?.data?.result || [], currentMallName);
    } catch (error) {
      console.error(error);
      toast.error('Liste yenilenemedi.');
    } finally {
      setListLoading(false);
    }
  };

  const handleCompleteRequest = async () => {
    if (!pendingComplete?.requestID || !pendingComplete?.poiID) {
      setConfirmCompleteOpen(false);
      setPendingComplete(null);
      return;
    }

    try {
      const response = await Services.PetKidSetRequestComplete(
        parsedUser.token,
        pendingComplete.requestID,
        pendingComplete.poiID
      );

      if (response?.data?.status) {
        toast.success('İşlem tamamlandı.');
        await handleRefreshCurrentList();
      } else {
        toast.error('İşlem tamamlanırken bir sorun oluştu.');
      }
    } catch (error) {
      console.error(error);
      toast.error('İşlem tamamlanırken bir sorun oluştu.');
    } finally {
      setConfirmCompleteOpen(false);
      setPendingComplete(null);
    }
  };

  const handleEditOpen = (row) => {
    setEditedData({
      checkroomID: row.checkroomID,
      inventoryCount: row.inventoryCount,
      additionalNote: row.additionalNote || '',
    });
    setEditModalOpen(true);
  };

  const handleSaveEditedData = async () => {
    try {
      const response = await api.post('flow/checkroom-update', {
        checkroomID: editedData.checkroomID,
        inventoryCount: Number(editedData.inventoryCount),
        additionalNote: editedData.additionalNote,
      });

      if (response?.data?.status) {
        toast.success('Vestiyer kaydı güncellendi.');
        setEditModalOpen(false);
        await handleRefreshCurrentList();
      } else {
        toast.error('Düzenleme sırasında bir hata oluştu.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Düzenleme sırasında bir hata oluştu.');
    }
  };

  const handleCancelConfirm = async () => {
    try {
      setCanceling(true);
      const response = await Services.CancelCheckRoom(cancelCheckroomId);
      if (response?.data?.status) {
        toast.success('Vestiyer işlemi iptal edildi.');
        await handleRefreshCurrentList();
      } else {
        toast.error('İptal sırasında bir hata oluştu.');
      }
    } catch (error) {
      console.error(error);
      toast.error('İptal sırasında bir hata oluştu.');
    } finally {
      setCanceling(false);
      setCancelModalOpen(false);
      setCancelCheckroomId(null);
    }
  };

  const vehicleHeaders = useMemo(
    () => [
      { id: 'no', label: 'No', alignRight: false, sx: { width: '20px' } },
      { id: 'conversationID', label: 'Conversation ID', alignRight: false },
      { id: 'requestID', label: 'Sipariş ID', alignRight: false },
      { id: 'locationInfo', label: 'Konum Bilgisi', alignRight: false },
      { id: 'name', label: 'Ad', alignRight: false },
      { id: 'lastname', label: 'Soyad', alignRight: false },
      { id: 'vehicleID', label: 'Araç No', alignRight: false },
      { id: 'packageCount', label: 'A. Adedi', alignRight: false },
      { id: 'totalPrice', label: 'Ödenen Ücret', alignRight: false },
      {
        id: 'createdDateTime',
        label: 'Talep Tarihi',
        alignRight: false,
        sortFunction: (row) => Number(row.createdDateTimeTs || 0),
      },
      {
        id: 'confirmedDateTime',
        label: 'Onaylanma Tarihi',
        alignRight: false,
        sortFunction: (row) => Number(row.confirmedDateTimeTs || 0),
      },
      { id: 'rentalDuration', label: 'Kiralama Süresi', alignRight: false },
      {
        id: 'status',
        label: 'Durum',
        alignRight: false,
        format: (value, row) => {
          const statusValue = row.displayStatus ?? value;
          const label =
            statusValue === 'payment-waiting' && row.paymentType === 'physical-pos'
              ? 'Fiziksel POS Onay Bekleniyor'
              : statusValue === 'payment-waiting' && row.paymentType === 'cash'
                ? 'Nakit Onay Bekleniyor'
              : statusLabels[statusValue] ?? statusValue;

          return renderStatusBadge(statusValue, label);
        },
      },
      {
        id: 'item',
        label: '',
        alignRight: false,
        format: (_value, row) => {
          if (row.status !== '4') return null;

          return (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setPendingComplete({
                  requestID: row.item.requestID,
                  poiID: row.item.poiID,
                });
                setConfirmCompleteOpen(true);
              }}
            >
              Tamamla
            </Button>
          );
        },
      },
    ],
    []
  );

  const checkroomHeaders = useMemo(
    () => [
      { id: 'no', label: 'No', alignRight: false, sx: { width: '20px' } },
      { id: 'pointTitle', label: 'Vestiyer Bilgisi', alignRight: false },
      { id: 'name', label: 'Ad', alignRight: false },
      { id: 'lastname', label: 'Soyad', alignRight: false },
      { id: 'phone', label: 'Telefon', alignRight: false },
      { id: 'checkroomID', label: 'Vest. No', alignRight: false },
      { id: 'inventoryCount', label: 'P. Adedi', alignRight: false },
      {
        id: 'createdDateTimeLabel',
        label: 'Oluşturma Tarihi',
        alignRight: false,
        sortFunction: (row) => Number(row.createdDateTime || 0),
      },
      {
        id: 'status',
        label: 'Durum',
        alignRight: false,
        format: (value) => {
          if (value === '1') {
            return renderStatusBadge('1', 'Tamamlandı');
          }

          if (value === '3') {
            return renderStatusBadge('4', 'İşlemde');
          }

          return renderStatusBadge('99', 'İptal');
        },
      },
      {
        id: 'settings',
        label: 'İşlemler',
        alignRight: false,
        format: (_value, row) => {
          if (row.status !== '3') return null;

          return (
            <Stack direction="column" spacing={0.5} alignItems="flex-start">
              <CheckDeliveryRoom checkroomID={row.checkroomID} />
              <Button size="small" onClick={() => handleEditOpen(row)}>
                Düzenle
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setCancelCheckroomId(row.checkroomID);
                  setCancelModalOpen(true);
                }}
              >
                İptal Et
              </Button>
            </Stack>
          );
        },
      },
    ],
    []
  );

  const serviceLabel = SERVICE_OPTIONS.find(
    (option) => option.value === currentService
  )?.label;

  return (
    <>
      <Helmet>
        <title>Easy Desk İşlemleri</title>
      </Helmet>

      <Stack sx={{ px: 3, py: 3 }}>
        <Stack mb={3}>
          <Typography variant="h4">Easy Desk İşlemleri</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>
            AVM ve hizmet seçerek ilgili kayıtları listeleyebilir, mevcut işlem
            akışlarını aynı ekran üzerinden yönetebilirsiniz.
          </Typography>
        </Stack>

        <Card sx={{ p: 3 }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems={isMobile ? 'stretch' : 'center'}
            mb={tableRows.length > 0 ? 3 : 0}
          >
            <Autocomplete
              fullWidth
              size="small"
              options={malls}
              value={selectedMallOption}
              onChange={(_event, value) => setSelectedMallId(value?.id || '')}
              getOptionLabel={(option) => option?.title || ''}
              isOptionEqualToValue={(option, value) =>
                normalizeId(option?.id) === normalizeId(value?.id)
              }
              noOptionsText="Sonuç bulunamadı"
              disablePortal
              ListboxProps={{
                style: {
                  maxHeight: 280,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="AVM Seçiniz"
                  placeholder="AVM ara"
                  size="small"
                />
              )}
              sx={{
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                },
              }}
              disabled={metaLoading}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="service-select-label">Hizmet Seçiniz</InputLabel>
              <Select
                labelId="service-select-label"
                value={selectedService}
                label="Hizmet Seçiniz"
                onChange={(event) => setSelectedService(event.target.value)}
                disabled={metaLoading}
                sx={{
                  backgroundColor: '#fff',
                }}
              >
                {SERVICE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              onClick={handleList}
              disabled={metaLoading || listLoading}
              sx={{
                minWidth: isMobile ? '100%' : 120,
                height: 40,
                px: 2.5,
                boxShadow: 'none',
              }}
            >
              Listele
            </Button>
          </Stack>

          {selectedMall ? (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2}
              alignItems={isMobile ? 'flex-start' : 'center'}
              sx={{
                mt: 2,
                mb: tableRows.length > 0 ? 1 : 0,
                p: 2,
                border: '1px solid #EAECF0',
                borderRadius: 2,
                backgroundColor: '#F9FAFB',
                justifyContent: 'flex-end',
                width: 'fit-content',
                marginLeft: "auto"
              }}
            >              
              {selectedMallQrLink ? (
                <Stack
                  sx={{
                    p: 1,
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    border: '1px solid #EAECF0',
                  }}
                >
                  <img
                    src={selectedMallQrLink}
                    alt={`${selectedMall.title || 'AVM'} QR`}
                    style={{ width: 140, height: 140, display: 'block' }}
                  />
                </Stack>
              ) : null}
            </Stack>
          ) : null}

          {currentService && currentMallName ? (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
              mt={1.5}
              mb={2}
              spacing={2}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={currentMallName} variant="outlined" />
                <Chip label={serviceLabel || '-'} color="primary" variant="outlined" />
              </Stack>

              {currentService !== 'checkroom' ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setApprovalOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(249, 250, 251, 1)',
                    border: '1px solid rgba(234, 236, 240, 1)',
                    color: 'rgba(52, 64, 84, 1)',
                    boxShadow: 'none',
                    fontWeight: '500',
                    minHeight: 38,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(249, 250, 251, 1)',
                      border: '1px solid rgb(193, 194, 196)',
                    },
                  }}
                >
                  Talepler
                  <span
                    style={{
                      width: '21px',
                      height: '21px',
                      borderRadius: '100%',
                      backgroundColor: 'rgba(255, 225, 225, 1)',
                      color: 'rgba(219, 57, 57, 1)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      display: 'flex',
                      marginLeft: '8px',
                    }}
                  >
                    {pendingApprovalRows.length}
                  </span>
                </Button>
              ) : null}
            </Stack>
          ) : null}

          {metaLoading || listLoading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%', py: 8 }}
            >
              <CircularProgress size={28} />
            </Stack>
          ) : tableRows.length > 0 ? (
            <DynamicTable
              data={tableRows}
              headers={currentService === 'checkroom' ? checkroomHeaders : vehicleHeaders}
              initialOrder={
                currentService === 'checkroom'
                  ? 'createdDateTimeLabel'
                  : 'confirmedDateTime'
              }
              initalOrderDirection="desc"
              isMobile={isMobile}
            />
          ) : (
            <Stack sx={{ py: 6 }} alignItems="center">
              <Typography sx={{ color: 'text.secondary' }}>
                Listelemek için AVM ve hizmet seçip "Listele" butonuna tıklayınız.
              </Typography>
            </Stack>
          )}
        </Card>
      </Stack>

      <CutomerVehicleApprovalModal
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        data={pendingApprovalRows}
        type={currentService === 'kid-car' ? 'kid-car' : 'pet-car'}
        getRows={handleRefreshCurrentList}
      />

      <Dialog
        open={confirmCompleteOpen}
        onClose={() => setConfirmCompleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>İşlem Onayı</DialogTitle>
        <DialogContent>
          Bu işlemi tamamlamak istediğinizden emin misiniz?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmCompleteOpen(false);
              setPendingComplete(null);
            }}
            variant="outlined"
          >
            Vazgeç
          </Button>
          <Button onClick={handleCompleteRequest} variant="contained">
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} fullWidth>
        <DialogTitle>Vestiyer Kaydını Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Paket Adedi"
              type="number"
              value={editedData.inventoryCount}
              onChange={(event) =>
                setEditedData((prev) => ({
                  ...prev,
                  inventoryCount: event.target.value,
                }))
              }
            />
            <TextField
              label="Ek Not"
              multiline
              minRows={3}
              value={editedData.additionalNote}
              onChange={(event) =>
                setEditedData((prev) => ({
                  ...prev,
                  additionalNote: event.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Kapat</Button>
          <Button onClick={handleSaveEditedData} variant="contained">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelModalOpen} onClose={() => setCancelModalOpen(false)}>
        <DialogTitle>Vestiyer İptali Onayı</DialogTitle>
        <DialogContent>
          <Typography>Vestiyeri iptal etmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelModalOpen(false)} disabled={canceling}>
            Kapat
          </Button>
          <Button onClick={handleCancelConfirm} disabled={canceling}>
            {canceling ? <CircularProgress size={20} /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
